import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  TipsAndUpdates as TipIcon,
  PriorityHigh as ImportantIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  PlayArrow,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { ContentBlock, CalloutVariant, ChecklistItem } from '../../../types/blocks';
import { StudentCredentials } from '../../../types/session.types';
import { mediaService } from '../../../services/media.service';

interface BlockRendererProps {
  blocks: ContentBlock[];
  interactive?: boolean;
  checkedItems?: Set<string>;
  onCheckItem?: (itemId: string) => void;
  credentials?: StudentCredentials;
}

const calloutConfig: Record<CalloutVariant, { color: string; bg: string; icon: React.ReactElement }> = {
  info: { color: '#3b82f6', bg: '#eff6ff', icon: <InfoIcon sx={{ fontSize: 20 }} /> },
  warning: { color: '#f59e0b', bg: '#fffbeb', icon: <WarningIcon sx={{ fontSize: 20 }} /> },
  tip: { color: '#22c55e', bg: '#f0fdf4', icon: <TipIcon sx={{ fontSize: 20 }} /> },
  important: { color: '#ef4444', bg: '#fef2f2', icon: <ImportantIcon sx={{ fontSize: 20 }} /> },
};

const headingVariant: Record<number, 'h4' | 'h5' | 'h6'> = {
  1: 'h4',
  2: 'h5',
  3: 'h6',
};

const sizeWidthMap: Record<string, string> = {
  small: '25%',
  medium: '50%',
  large: '75%',
  full: '100%',
};

const alignMap: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

function useResolvedUrl(content: string): string {
  const [resolved, setResolved] = useState(() =>
    content && (content.startsWith('http://') || content.startsWith('https://'))
      ? content
      : '',
  );

  useEffect(() => {
    if (!content) {
      setResolved('');
      return;
    }
    let cancelled = false;
    mediaService.resolveMediaUrl(content).then((url) => {
      if (!cancelled) setResolved(url);
    });
    return () => { cancelled = true; };
  }, [content]);

  return resolved;
}

// Template variable map: {{variable_name}} → credential field
const TEMPLATE_VARS: Record<string, string> = {
  tenant_url: 'tenantUrl',
  tenant_admin_email: 'tenantAdminEmail',
  tenant_admin_password: 'tenantAdminPassword',
  sharing_portal_url: 'sharingPortalUrl',
  skytap_portal_password: 'skytapPortalPassword',
};

function replaceTemplateVars(
  content: string,
  credentials?: BlockRendererProps['credentials'],
): string {
  if (!credentials || !content) return content;
  return content.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
    const field = TEMPLATE_VARS[varName];
    if (!field) return match; // Unknown variable — leave as-is
    const value = credentials[field as keyof typeof credentials];
    return value || match; // If null, leave the token visible
  });
}

function RenderBlock({
  block,
  blocks,
  interactive,
  checkedItems,
  onCheckItem,
  credentials,
}: {
  block: ContentBlock;
  blocks: ContentBlock[];
  interactive?: boolean;
  checkedItems?: Set<string>;
  onCheckItem?: (itemId: string) => void;
  credentials?: BlockRendererProps['credentials'];
}) {
  switch (block.type) {
    case 'text':
      return (
        <Box sx={{
          '& p': { mt: 0, mb: 1, lineHeight: 1.7, fontSize: '0.975rem' },
          '& p:last-child': { mb: 0 },
          '& strong': { fontWeight: 600 },
          '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
          '& code': {
            bgcolor: 'action.hover',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: '0.85em',
            fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
          },
          '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
          '& li': { mb: 0.25, fontSize: '0.975rem', lineHeight: 1.7 },
          '& blockquote': {
            borderLeft: '3px solid',
            borderColor: 'divider',
            pl: 2,
            ml: 0,
            color: 'text.secondary',
            fontStyle: 'italic',
          },
        }}>
          <ReactMarkdown>{replaceTemplateVars(block.content, credentials)}</ReactMarkdown>
        </Box>
      );

    case 'heading': {
      const level = (block.meta?.level as number) || 2;
      return (
        <Typography variant={headingVariant[level] || 'h5'} sx={{
          fontWeight: 600,
          '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.85em', fontFamily: 'monospace' },
          '& strong': { fontWeight: 700 },
        }}>
          <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>
            {replaceTemplateVars(block.content, credentials)}
          </ReactMarkdown>
        </Typography>
      );
    }

    case 'callout': {
      const variant = (block.meta?.variant as CalloutVariant) || 'info';
      const config = calloutConfig[variant];
      return (
        <Box
          sx={{
            borderLeft: `3px solid ${config.color}`,
            bgcolor: config.bg,
            borderRadius: 1,
            p: 2,
            display: 'flex',
            gap: 1.5,
          }}
        >
          <Box sx={{ color: config.color, mt: 0.25, flexShrink: 0 }}>{config.icon}</Box>
          <Box sx={{ flex: 1 }}>
            {block.meta?.title && (
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {block.meta.title}
              </Typography>
            )}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {replaceTemplateVars(block.content, credentials)}
            </Typography>
          </Box>
        </Box>
      );
    }

    case 'code': {
      const language = (block.meta?.language as string) || '';
      const handleCopy = () => navigator.clipboard.writeText(block.content);
      return (
        <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 0.5,
              bgcolor: '#1e1e2e',
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6875rem' }}>
              {language}
            </Typography>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopy} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              bgcolor: '#1e1e2e',
              color: '#e2e8f0',
              fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
              fontSize: '0.8125rem',
              lineHeight: 1.6,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {block.content}
          </Box>
        </Box>
      );
    }

    case 'image':
      return <ImageBlock block={block} allBlocks={blocks} />;

    case 'image-grid':
      return <ImageGridBlock block={block} />;

    case 'video':
      return <VideoBlock block={block} />;

    case 'checklist': {
      const items: ChecklistItem[] = block.meta?.items || [];
      const checkedCount = items.filter((item) => checkedItems?.has(item.id)).length;
      return (
        <Box>
          {items.map((item) => {
            const isChecked = checkedItems?.has(item.id) || false;
            return (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    checked={isChecked}
                    disabled={!interactive}
                    onChange={() => onCheckItem?.(item.id)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      textDecoration: isChecked ? 'line-through' : 'none',
                      color: isChecked ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {item.text}
                  </Typography>
                }
                sx={{ display: 'flex', ml: 0, mb: 0.25 }}
              />
            );
          })}
          {items.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {checkedCount} of {items.length} tasks complete
            </Typography>
          )}
        </Box>
      );
    }

    case 'divider':
      return <Divider sx={{ my: 1 }} />;

    default:
      return null;
  }
}

function ImageBlock({ block, allBlocks }: { block: ContentBlock; allBlocks: ContentBlock[] }) {
  const src = useResolvedUrl(block.content);
  const [open, setOpen] = useState(false);

  const size = (block.meta?.size as string) || 'large';
  const align = (block.meta?.align as string) || 'center';
  const borderStyle = (block.meta?.borderStyle as string) || 'none';

  // Gallery: all image blocks with content
  const imageBlocks = allBlocks.filter((b) => b.type === 'image' && b.content);
  const hasGallery = imageBlocks.length > 1;
  const initialIndex = imageBlocks.findIndex((b) => b.id === block.id);
  const [currentIndex, setCurrentIndex] = useState(initialIndex >= 0 ? initialIndex : 0);

  // Resolve all image URLs for gallery lightbox
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const resolve = async () => {
      const entries: Record<string, string> = {};
      await Promise.all(
        imageBlocks.map(async (b) => {
          const url = await mediaService.resolveMediaUrl(b.content);
          entries[b.id] = url;
        }),
      );
      if (!cancelled) setResolvedUrls(entries);
    };
    resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset index when opening
  const handleOpen = useCallback(() => {
    setCurrentIndex(initialIndex >= 0 ? initialIndex : 0);
    setOpen(true);
  }, [initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !hasGallery) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setCurrentIndex((i) => (i > 0 ? i - 1 : imageBlocks.length - 1));
      else if (e.key === 'ArrowRight') setCurrentIndex((i) => (i < imageBlocks.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasGallery, imageBlocks.length]);

  const borderSx = borderStyle === 'shadow'
    ? { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }
    : borderStyle === 'bordered'
      ? { border: '1px solid', borderColor: 'divider', borderRadius: '8px' }
      : {};

  const currentGalleryBlock = imageBlocks[currentIndex];
  const currentGallerySrc = currentGalleryBlock ? resolvedUrls[currentGalleryBlock.id] : '';

  return (
    <Box sx={{ display: 'flex', justifyContent: alignMap[align] || 'center' }}>
      <Box sx={{ width: sizeWidthMap[size] || '75%', maxWidth: '100%' }}>
        {src && (
          <Box
            sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              ...borderSx,
              '&:hover img': { transform: 'scale(1.01)' },
            }}
          >
            <img
              src={src}
              alt={block.meta?.alt || ''}
              onClick={handleOpen}
              style={{
                width: '100%',
                objectFit: 'contain',
                borderRadius: 8,
                cursor: 'zoom-in',
                transition: 'transform 0.2s ease',
                display: 'block',
              }}
            />
          </Box>
        )}
        {block.meta?.caption && (
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 0.75, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem', lineHeight: 1.4 }}
          >
            {block.meta.caption}
          </Typography>
        )}
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth={false}
          fullWidth
          sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.85)' } }}
          slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', maxWidth: '95vw', m: 'auto' } } }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>
          {hasGallery && (
            <IconButton
              onClick={() => setCurrentIndex((i) => (i > 0 ? i - 1 : imageBlocks.length - 1))}
              sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
            >
              <ChevronLeft />
            </IconButton>
          )}
          {hasGallery && (
            <IconButton
              onClick={() => setCurrentIndex((i) => (i < imageBlocks.length - 1 ? i + 1 : 0))}
              sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
            >
              <ChevronRight />
            </IconButton>
          )}
          <DialogContent sx={{ textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {currentGallerySrc && (
              <img
                src={currentGallerySrc}
                alt={currentGalleryBlock?.meta?.alt || ''}
                style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
              />
            )}
            {currentGalleryBlock?.meta?.caption && (
              <Typography variant="body2" sx={{ mt: 1, color: '#fff', fontStyle: 'italic' }}>
                {currentGalleryBlock.meta.caption}
              </Typography>
            )}
            {hasGallery && (
              <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.6)' }}>
                {currentIndex + 1} of {imageBlocks.length}
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
}

function ImageGridBlock({ block }: { block: ContentBlock }) {
  const columns = (block.meta?.columns as number) || 3;
  const images: Array<{ id: string; content: string; caption?: string; alt?: string }> = block.meta?.images || [];
  const validImages = images.filter((img) => img.content);

  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const entries: Record<string, string> = {};
      await Promise.all(
        validImages.map(async (img) => {
          const url = await mediaService.resolveMediaUrl(img.content);
          entries[img.id] = url;
        }),
      );
      if (!cancelled) setResolvedUrls(entries);
    };
    if (validImages.length > 0) resolve();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validImages.map((i) => i.content).join(',')]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen || validImages.length <= 1) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i > 0 ? i - 1 : validImages.length - 1));
      else if (e.key === 'ArrowRight') setLightboxIndex((i) => (i < validImages.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, validImages.length]);

  const currentImg = validImages[lightboxIndex];
  const currentSrc = currentImg ? resolvedUrls[currentImg.id] : '';

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 2 }}>
        {validImages.map((img, idx) => (
          <Box key={img.id}>
            <Box sx={{
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'zoom-in',
              '&:hover img': { transform: 'scale(1.01)' },
            }}>
              {resolvedUrls[img.id] && (
                <img
                  src={resolvedUrls[img.id]}
                  alt={img.alt || ''}
                  onClick={() => openLightbox(idx)}
                  style={{ width: '100%', objectFit: 'cover', borderRadius: 8, transition: 'transform 0.2s', display: 'block' }}
                />
              )}
            </Box>
            {img.caption && (
              <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem' }}>
                {img.caption}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        fullWidth
        sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.85)' } }}
        slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none', maxWidth: '95vw', m: 'auto' } } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
        >
          <CloseIcon />
        </IconButton>
        {validImages.length > 1 && (
          <IconButton
            onClick={() => setLightboxIndex((i) => (i > 0 ? i - 1 : validImages.length - 1))}
            sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <ChevronLeft />
          </IconButton>
        )}
        {validImages.length > 1 && (
          <IconButton
            onClick={() => setLightboxIndex((i) => (i < validImages.length - 1 ? i + 1 : 0))}
            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <ChevronRight />
          </IconButton>
        )}
        <DialogContent sx={{ textAlign: 'center', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {currentSrc && (
            <img
              src={currentSrc}
              alt={currentImg?.alt || ''}
              style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
          {currentImg?.caption && (
            <Typography variant="body2" sx={{ mt: 1, color: '#fff', fontStyle: 'italic' }}>
              {currentImg.caption}
            </Typography>
          )}
          {validImages.length > 1 && (
            <Typography variant="caption" sx={{ mt: 1, color: 'rgba(255,255,255,0.6)' }}>
              {lightboxIndex + 1} of {validImages.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function VideoBlock({ block }: { block: ContentBlock }) {
  const src = useResolvedUrl(block.content);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showOverlay, setShowOverlay] = useState(true);

  const size = (block.meta?.size as string) || 'large';
  const align = (block.meta?.align as string) || 'center';

  const handlePlay = () => {
    videoRef.current?.play();
    setShowOverlay(false);
  };

  const handleVideoPlay = () => setShowOverlay(false);
  const handleVideoPause = () => {
    if (videoRef.current && videoRef.current.currentTime === 0) {
      setShowOverlay(true);
    }
  };
  const handleVideoEnded = () => setShowOverlay(true);

  // Reset overlay when src changes
  useEffect(() => { setShowOverlay(true); }, [src]);

  return (
    <Box sx={{ display: 'flex', justifyContent: alignMap[align] || 'center' }}>
      <Box sx={{ width: sizeWidthMap[size] || '75%', maxWidth: '100%' }}>
        {src && (
          <Box sx={{ borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
            <video
              ref={videoRef}
              src={src}
              controls
              onPlay={handleVideoPlay}
              onPause={handleVideoPause}
              onEnded={handleVideoEnded}
              style={{ width: '100%', borderRadius: 8, display: 'block' }}
            />
            {showOverlay && (
              <Box
                onClick={handlePlay}
                sx={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: 64, height: 64, borderRadius: '50%',
                  bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)', transform: 'translate(-50%, -50%) scale(1.1)' },
                }}
              >
                <PlayArrow sx={{ color: '#fff', fontSize: 32 }} />
              </Box>
            )}
          </Box>
        )}
        {block.meta?.caption && (
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 0.75, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.8rem', lineHeight: 1.4 }}
          >
            {block.meta.caption}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

const BlockRenderer: React.FC<BlockRendererProps> = ({ blocks, interactive, checkedItems, onCheckItem, credentials }) => {
  if (!blocks || blocks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2, textAlign: 'center' }}>
        No content blocks yet
      </Typography>
    );
  }

  const sortedBlocks = blocks.slice().sort((a, b) => a.order - b.order);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedBlocks.map((block) => (
          <RenderBlock
            key={block.id}
            block={block}
            blocks={sortedBlocks}
            interactive={interactive}
            checkedItems={checkedItems}
            onCheckItem={onCheckItem}
            credentials={credentials}
          />
        ))}
    </Box>
  );
};

export default BlockRenderer;
